/**
 * @file CentralConversoes.jsx
 * @description Aba dentro de SystemHubClientes — fila de tickets de conversão
 * (lead fechado + contrato assinado) aguardando o CS criar o tenant.
 *
 * Reconstrução (Fase 2.2 — "cockpit do CS"):
 *   - Barra de métricas/SLA: pendentes, receita travada, espera média e o
 *     ticket mais antigo, calculadas dos itens carregados.
 *   - Priorização inteligente ("Comece por aqui"): destaca o ticket de maior
 *     urgência, ponderando horas de espera × receita mensal travada.
 *   - Busca (empresa / responsável / vendedor) + ordenação (urgência, recentes,
 *     maior valor, empresa A–Z), tudo client-side sobre o que já foi carregado.
 *   - Card expansível com TIMELINE derivada dos campos de data da própria
 *     conversão (sem endpoint novo) + detalhes e copiar contato.
 *   - Abas por grupo com contadores + paginação server-side (mantidos).
 *   - Realtime opcional via Socket.io (prop `socket`) — mantido.
 *
 * NOVO (portão de início de cobrança):
 *   - "Criar Tenant Agora" não dispara mais a criação direto. Abre um modal
 *     obrigatório onde o CS escolhe 30/60/90 dias a partir do cadastro
 *     (= agora) ou digita uma data. Isso vai no payload de /criar-tenant e
 *     vira billing_start_date em billing_client_settings, o portão que
 *     libera geração de fatura (manual ou automática) para o tenant.
 *
 * Consome:
 *   GET  /api/vendas/conversoes?grupo=<g>&limit=<n>&offset=<n>
 *          → { items, total, counts:{pendentes,assinatura,encerradas} }
 *   POST /api/vendas/conversoes/:id/criar-tenant
 *          body: { billing_start_mode, billing_start_date_custom? }
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FaInbox, FaClock, FaCheckCircle, FaExclamationTriangle, FaBuilding,
  FaUserTie, FaFileSignature, FaSync, FaTimesCircle, FaSearch,
  FaChevronDown, FaChevronRight, FaCopy, FaBolt, FaMoneyBillWave,
  FaHourglassHalf, FaSort, FaEnvelope, FaWhatsapp, FaIdCard, FaBan, FaHistory,
  FaCalendarAlt, FaTimes,
} from 'react-icons/fa';
import './CentralConversoes.css';

const PAGE = 20;

const GRUPOS = [
  { key: 'pendentes',  label: 'Aguardando ação', icon: FaExclamationTriangle },
  { key: 'assinatura', label: 'Em assinatura',   icon: FaFileSignature },
  { key: 'encerradas', label: 'Encerradas',      icon: FaTimesCircle },
];

const SORTS = [
  { key: 'urgencia', label: 'Mais urgentes' },
  { key: 'recentes', label: 'Mais recentes' },
  { key: 'valor',    label: 'Maior valor' },
  { key: 'empresa',  label: 'Empresa (A–Z)' },
];

const STATUS_META = {
  aguardando_assinatura: { label: 'Aguardando assinatura', tone: 'info' },
  assinado:              { label: 'Assinado',              tone: 'info' },
  aguardando_cs:         { label: 'Aguardando CS',         tone: 'amber' },
  em_criacao_tenant:     { label: 'Criando tenant…',       tone: 'amber' },
  recusado:              { label: 'Recusado pelo cliente', tone: 'danger' },
  cancelado:             { label: 'Cancelado',             tone: 'muted' },
  rascunho:              { label: 'Rascunho',              tone: 'muted' },
};

const SLA_LIMITE_H = 4; // acima disso, o ticket entra em alerta visual

// Opções do portão de início de cobrança.
const BILLING_START_OPTIONS = [
  { key: '30', label: 'D+30', hint: '30 dias a partir de hoje' },
  { key: '60', label: 'D+60', hint: '60 dias a partir de hoje' },
  { key: '90', label: 'D+90', hint: '90 dias a partir de hoje' },
  { key: 'custom', label: 'Data personalizada', hint: 'Escolher uma data específica' },
];

const formatCurrency = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
};
const hoursSince = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 3_600_000);
};
// Espera relevante do ticket: desde que caiu no CS; senão, desde o envio.
const waitHoursOf = (c) => hoursSince(c.data_aguardando_cs) ?? hoursSince(c.data_envio_assinatura) ?? 0;
// Formata uma duração em horas de forma humana ("3h", "2d 4h").
const humanHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return '<1h';
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  const r = h % 24;
  return r ? `${d}d ${r}h` : `${d}d`;
};

// Data mínima aceita no seletor de data personalizada (hoje, formato YYYY-MM-DD).
const todayYMD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Pré-visualização da data calculada, só para o CS conferir antes de confirmar.
const previewBillingDate = (mode, customDate) => {
  if (mode === 'custom') {
    if (!customDate) return null;
    const d = new Date(`${customDate}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
  }
  if (!mode) return null;
  const d = new Date();
  d.setDate(d.getDate() + Number(mode));
  return d.toLocaleDateString('pt-BR');
};

// Score de prioridade: espera pesa direto; receita entra como multiplicador
// suave (cada R$ 500/mês ≈ +1 ponto). Empurra pro topo quem espera muito E paga bem.
const priorityScore = (c) => waitHoursOf(c) * (1 + (Number(c.valor_mensal || 0) / 500));

// Passos da timeline derivados dos carimbos de data da conversão.
function buildTimeline(c) {
  const steps = [
    { key: 'envio',    label: 'Contrato enviado',      icon: FaFileSignature, date: c.data_envio_assinatura },
    { key: 'assinado', label: 'Assinado pelo cliente', icon: FaCheckCircle,   date: c.data_assinatura },
    { key: 'cs',       label: 'Na fila do CS',         icon: FaHourglassHalf, date: c.data_aguardando_cs },
    { key: 'tenant',   label: 'Tenant criado',         icon: FaBuilding,      date: c.data_criacao_tenant },
  ];
  if (c.data_recusa_cancelamento) {
    steps.push({
      key: 'fim', danger: true, icon: FaBan,
      label: c.motivo_recusa_cancelamento || 'Encerrado',
      date: c.data_recusa_cancelamento,
    });
  }
  return steps;
}

function Timeline({ conversao }) {
  const steps = buildTimeline(conversao);
  return (
    <ol className="cc-timeline">
      {steps.map((s) => {
        const done = Boolean(s.date);
        const Icon = s.icon;
        return (
          <li key={s.key} className={`cc-tl-item ${done ? 'is-done' : 'is-pending'} ${s.danger ? 'is-danger' : ''}`}>
            <span className="cc-tl-dot"><Icon size={11} /></span>
            <div className="cc-tl-body">
              <span className="cc-tl-label">{s.label}</span>
              <span className="cc-tl-date">{done ? formatDateTime(s.date) : 'pendente'}</span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// NOVO: modal obrigatório de início de cobrança. Estilos inline de propósito
// (não tenho o CentralConversoes.css em mãos, então não arrisco depender de
// classes que talvez não existam lá). Visualmente neutro, não deve destoar.
// ─────────────────────────────────────────────────────────────────────────
function BillingStartModal({ open, empresa, onConfirm, onCancel, submitting }) {
  const [mode, setMode] = useState('30');
  const [customDate, setCustomDate] = useState('');

  useEffect(() => {
    if (open) { setMode('30'); setCustomDate(''); }
  }, [open]);

  if (!open) return null;

  const isCustomInvalid = mode === 'custom' && !customDate;
  const preview = previewBillingDate(mode, customDate);

  const handleConfirm = () => {
    if (isCustomInvalid) {
      toast.warn('Selecione a data personalizada para o início da cobrança.');
      return;
    }
    onConfirm({
      billing_start_mode: mode,
      billing_start_date_custom: mode === 'custom' ? customDate : undefined,
    });
  };

  return (
    <div className="cc-modal-backdrop" onMouseDown={onCancel}>
      <div className="cc-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cc-modal__header">
          <div className="cc-modal__title">
            <FaCalendarAlt />
            <div className="cc-modal__title-text">
              <strong>Início da cobrança</strong>
              <span>{empresa}</span>
            </div>
          </div>
          <button type="button" className="cc-modal__close" onClick={onCancel} title="Cancelar">
            <FaTimes />
          </button>
        </div>

        <div className="cc-modal__body">
          <p className="cc-modal__desc">
            Defina quando a cobrança deste cliente deve começar. Nenhuma fatura, manual ou
            automática, será gerada antes dessa data.
          </p>

          <div className="cc-modal__options">
            {BILLING_START_OPTIONS.map((opt) => {
              const active = mode === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  className={`cc-modal__option ${active ? 'is-active' : ''}`}
                  onClick={() => setMode(opt.key)}
                >
                  <div className="cc-modal__option-label">{opt.label}</div>
                  <div className="cc-modal__option-hint">{opt.hint}</div>
                </button>
              );
            })}
          </div>

          {mode === 'custom' && (
            <label className="cc-modal__field">
              Data de início
              <input
                type="date"
                value={customDate}
                min={todayYMD()}
                onChange={(e) => setCustomDate(e.target.value)}
              />
            </label>
          )}

          {preview && (
            <div className="cc-modal__preview">
              <FaCheckCircle />
              Cobrança liberada a partir de&nbsp;<b>{preview}</b>
            </div>
          )}
        </div>

        <div className="cc-modal__footer">
          <button type="button" className="cc-btn cc-btn--ghost" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="cc-btn cc-btn--primary"
            onClick={handleConfirm}
            disabled={submitting || isCustomInvalid}
          >
            {submitting ? (<><span className="cc-spin" /> Criando tenant…</>) : (<><FaCheckCircle /> Confirmar e criar tenant</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

function TicketCard({ conversao, onOpenBillingModal, creating, expanded, onToggle, onCopy, isPriority, onReenviar, reenviando }) {
  const meta = STATUS_META[conversao.status] || { label: conversao.status, tone: 'muted' };
  const wait = conversao.status === 'aguardando_cs' ? waitHoursOf(conversao) : null;
  const slaAlerta = wait !== null && wait >= SLA_LIMITE_H;

  // Reenvio pelo OUTRO canal (só na etapa de assinatura). O canal usado no envio
  // fica em auth_method; oferecemos o complementar, se houver contato para ele.
  const podeReenviar = conversao.status === 'aguardando_assinatura';
  const outroCanal = conversao.auth_method === 'whatsapp' ? 'email' : 'whatsapp';
  const temContatoOutro = outroCanal === 'email' ? !!conversao.responsavel_email : !!conversao.responsavel_telefone;

  return (
    <div className={`cc-card ${expanded ? 'is-open' : ''} ${isPriority ? 'is-priority' : ''}`}>
      {isPriority && (
        <div className="cc-card__flag"><FaBolt size={11} /> Comece por aqui</div>
      )}

      <button type="button" className="cc-card__head" onClick={onToggle} aria-expanded={expanded}>
        <span className={`cc-card__chev ${expanded ? 'is-open' : ''}`}><FaChevronRight size={12} /></span>

        <span className="cc-card__ident">
          <span className="cc-card__empresa"><FaBuilding size={13} /> {conversao.nome_empresa}</span>
          <span className="cc-card__meta">
            <span><FaUserTie size={10} /> {conversao.responsavel_nome || '—'}</span>
            <span className="cc-dot-sep">·</span>
            <span className="cc-strong">{formatCurrency(conversao.valor_mensal)}/mês</span>
            <span className="cc-dot-sep">·</span>
            <span>Plano: {conversao.plano_id}</span>
          </span>
        </span>

        <span className="cc-card__right">
          {wait !== null && (
            <span className={`cc-wait ${slaAlerta ? 'is-alert' : ''}`} title="Tempo de espera">
              <FaClock size={10} /> {humanHours(wait)}
            </span>
          )}
          <span className={`cc-badge cc-badge--${meta.tone}`}>{meta.label}</span>
        </span>
      </button>

      {slaAlerta && !expanded && (
        <div className="cc-sla-strip">
          <FaExclamationTriangle size={11} /> Cliente já pagou e espera há {humanHours(wait)} — priorize.
        </div>
      )}

      {expanded && (
        <div className="cc-card__panel">
          <div className="cc-panel-grid">
            <div className="cc-panel-col">
              <div className="cc-panel-title"><FaHistory size={11} /> Linha do tempo</div>
              <Timeline conversao={conversao} />
            </div>

            <div className="cc-panel-col">
              <div className="cc-panel-title"><FaIdCard size={11} /> Detalhes</div>
              <dl className="cc-details">
                <div><dt>Documento</dt><dd>{conversao.documento_empresa || '—'}</dd></div>
                <div><dt>Licenças</dt><dd>{conversao.max_licencas ?? '—'}</dd></div>
                <div><dt>Vendedor</dt><dd>{conversao.vendedor_nome || '—'}</dd></div>
                <div><dt>Assinatura</dt><dd>{conversao.auth_method === 'whatsapp' ? 'WhatsApp' : 'E-mail'}</dd></div>
              </dl>

              {(conversao.responsavel_email || conversao.responsavel_telefone) && (
                <div className="cc-contacts">
                  {conversao.responsavel_email && (
                    <button type="button" className="cc-chip" onClick={() => onCopy(conversao.responsavel_email, 'E-mail')}>
                      <FaEnvelope size={11} /> {conversao.responsavel_email} <FaCopy size={10} className="cc-chip__copy" />
                    </button>
                  )}
                  {conversao.responsavel_telefone && (
                    <button type="button" className="cc-chip" onClick={() => onCopy(conversao.responsavel_telefone, 'Telefone')}>
                      <FaWhatsapp size={11} /> {conversao.responsavel_telefone} <FaCopy size={10} className="cc-chip__copy" />
                    </button>
                  )}
                </div>
              )}

              {conversao.condicoes_comerciais && (
                <div className="cc-condicoes">
                  <span className="cc-condicoes__lbl">Condições comerciais</span>
                  <p>{conversao.condicoes_comerciais}</p>
                </div>
              )}
            </div>
          </div>

          {podeReenviar && temContatoOutro && (
            <div className="cc-card__actions">
              <button type="button" className="cc-btn cc-btn--ghost" disabled={reenviando}
                onClick={() => onReenviar(conversao.id, outroCanal)}
                title={`Enviar também por ${outroCanal === 'whatsapp' ? 'WhatsApp' : 'e-mail'}`}>
                {reenviando
                  ? 'Reenviando…'
                  : (<>{outroCanal === 'whatsapp' ? <FaWhatsapp /> : <FaEnvelope />} Reenviar por {outroCanal === 'whatsapp' ? 'WhatsApp' : 'E-mail'}</>)}
              </button>
            </div>
          )}

          {conversao.status === 'aguardando_cs' && (
            <div className="cc-card__actions">
              <button type="button" className="cc-btn cc-btn--primary" disabled={creating} onClick={() => onOpenBillingModal(conversao)}>
                {creating ? (<><span className="cc-spin" /> Criando tenant…</>) : (<><FaCheckCircle /> Criar Tenant Agora</>)}
              </button>
            </div>
          )}
          {conversao.status === 'em_criacao_tenant' && (
            <div className="cc-card__processing"><FaClock /> Em processamento por {conversao.cs_nome || 'CS'}…</div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = 'default', hint }) {
  return (
    <div className={`cc-stat cc-stat--${tone}`}>
      <span className="cc-stat__icon"><Icon size={14} /></span>
      <span className="cc-stat__body">
        <span className="cc-stat__value">{value}</span>
        <span className="cc-stat__label">{label}</span>
        {hint && <span className="cc-stat__hint">{hint}</span>}
      </span>
    </div>
  );
}

export default function CentralConversoes({ onTenantCreated, socket }) {
  const [tab, setTab] = useState('pendentes');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pendentes: 0, assinatura: 0, encerradas: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [creatingId, setCreatingId] = useState(null);
  const [reenviandoId, setReenviandoId] = useState(null);
  const [pulse, setPulse] = useState(false);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('urgencia');
  const [expandedId, setExpandedId] = useState(null);

  // NOVO: conversão selecionada para o modal de início de cobrança.
  const [billingModalConversao, setBillingModalConversao] = useState(null);

  const tabRef = useRef(tab);
  tabRef.current = tab;
  const debounceRef = useRef(null);
  const searchRef = useRef(null);

  const fetchPage = useCallback(async (grupo, offset, replace) => {
    if (replace) setLoading(true); else setLoadingMore(true);
    try {
      const { data } = await api.get('/api/vendas/conversoes', { params: { grupo, limit: PAGE, offset } });
      const novos = Array.isArray(data.items) ? data.items : [];
      setCounts(data.counts || { pendentes: 0, assinatura: 0, encerradas: 0 });
      setTotal(Number(data.total || 0));
      setItems((prev) => (replace ? novos : [...prev, ...novos]));
    } catch {
      if (replace) setItems([]);
      toast.error('Não foi possível carregar a Central de Conversões.');
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  }, []);

  // Troca de aba → recarrega do zero e recolhe qualquer card aberto.
  useEffect(() => { setExpandedId(null); fetchPage(tab, 0, true); }, [tab, fetchPage]);

  // Realtime: qualquer evento refaz a página atual (debounced); ticket novo
  // fora da aba de pendentes faz o badge pulsar.
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return undefined;
    const refetch = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchPage(tabRef.current, 0, true), 600);
    };
    const onNovoTicket = () => {
      if (tabRef.current !== 'pendentes') { setPulse(true); setTimeout(() => setPulse(false), 4000); }
      refetch();
    };
    socket.on('conversoes:novo_ticket', onNovoTicket);
    socket.on('conversoes:atualizado', refetch);
    socket.on('conversoes:recusado', refetch);
    return () => {
      socket.off('conversoes:novo_ticket', onNovoTicket);
      socket.off('conversoes:atualizado', refetch);
      socket.off('conversoes:recusado', refetch);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [socket, fetchPage]);

  // Atalho: "/" foca a busca; Esc recolhe o card aberto.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault(); searchRef.current?.focus();
      } else if (e.key === 'Escape') {
        setExpandedId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // NOVO: abre o modal em vez de criar direto.
  const handleOpenBillingModal = (conversao) => {
    setBillingModalConversao(conversao);
  };

  // NOVO: só chama a API depois que o CS confirmou o início da cobrança no modal.
  const handleConfirmCreateTenant = async (billingPayload) => {
    if (!billingModalConversao) return;
    const conversaoId = billingModalConversao.id;

    setCreatingId(conversaoId);
    try {
      const { data } = await api.post(`/api/vendas/conversoes/${conversaoId}/criar-tenant`, billingPayload);
      toast.success(`Tenant #${data.tenant_id} criado com sucesso! Cobrança liberada a partir de ${data.billing_start_date?.split('-').reverse().join('/') || '—'}.`);
      setBillingModalConversao(null);
      await fetchPage(tab, 0, true);
      onTenantCreated?.(data.tenant_id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao criar o tenant.');
    } finally {
      setCreatingId(null);
    }
  };

  const copyToClipboard = useCallback((txt, label) => {
    if (!txt) return;
    navigator.clipboard?.writeText(txt)
      .then(() => toast.success(`${label} copiado.`))
      .catch(() => toast.info(txt));
  }, []);

  const handleReenviar = async (conversaoId, canal) => {
    setReenviandoId(conversaoId);
    try {
      const { data } = await api.post(`/api/vendas/conversoes/${conversaoId}/reenviar`, { canal });
      toast.success(`Contrato reenviado por ${canal === 'whatsapp' ? 'WhatsApp' : 'e-mail'}${data.via === 'zapi' ? ' (via Z-API)' : ''}.`);
      await fetchPage(tab, 0, true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Falha ao reenviar o contrato.');
    } finally {
      setReenviandoId(null);
    }
  };

  // ── Busca + ordenação (client-side sobre o que já foi carregado) ──
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = items;
    if (q) {
      arr = arr.filter((c) =>
        [c.nome_empresa, c.responsavel_nome, c.vendedor_nome, c.plano_id]
          .some((f) => String(f || '').toLowerCase().includes(q)));
    }
    const cmp = {
      urgencia: (a, b) => priorityScore(b) - priorityScore(a),
      recentes: (a, b) => new Date(b.data_atualizacao || b.data_envio_assinatura || 0) - new Date(a.data_atualizacao || a.data_envio_assinatura || 0),
      valor:    (a, b) => Number(b.valor_mensal || 0) - Number(a.valor_mensal || 0),
      empresa:  (a, b) => String(a.nome_empresa || '').localeCompare(String(b.nome_empresa || ''), 'pt-BR'),
    }[sort] || (() => 0);
    return [...arr].sort(cmp);
  }, [items, query, sort]);

  // ── Métricas do topo (calculadas dos itens carregados do grupo ativo) ──
  const metrics = useMemo(() => {
    const acionaveis = items.filter((c) => c.status === 'aguardando_cs');
    const receita = items.reduce((s, c) => s + Number(c.valor_mensal || 0), 0);
    const esperas = acionaveis.map(waitHoursOf);
    const media = esperas.length ? Math.round(esperas.reduce((a, b) => a + b, 0) / esperas.length) : null;
    const maisAntigo = esperas.length ? Math.max(...esperas) : null;
    return { receita, media, maisAntigo, acionaveisN: acionaveis.length };
  }, [items]);

  // ── Priorização inteligente: o ticket acionável de maior score ──
  const priorityId = useMemo(() => {
    if (tab !== 'pendentes') return null;
    const acionaveis = items.filter((c) => c.status === 'aguardando_cs');
    if (!acionaveis.length) return null;
    return acionaveis.reduce((top, c) => (priorityScore(c) > priorityScore(top) ? c : top)).id;
  }, [items, tab]);
  const priorityTicket = priorityId ? items.find((c) => c.id === priorityId) : null;

  const hasMore = items.length < total;
  const parcial = hasMore; // métricas só enxergam o que foi carregado

  return (
    <div className="cc-root">
      {/* ── Topbar ── */}
      <div className="cc-topbar">
        <div className="cc-topbar__title">
          <span className="cc-topbar__icon"><FaInbox /></span>
          <div>
            <h2>Central de Conversões</h2>
            <span>{counts.pendentes} ticket(s) aguardando ação do CS</span>
          </div>
        </div>
        <button type="button" className="cc-btn cc-btn--ghost" onClick={() => fetchPage(tab, 0, true)}>
          <FaSync /> Atualizar
        </button>
      </div>

      {/* ── Métricas / SLA ── */}
      <div className="cc-stats">
        <StatCard icon={FaExclamationTriangle} tone="accent" label="Aguardando ação" value={counts.pendentes} />
        <StatCard icon={FaMoneyBillWave} tone="money" label={parcial ? 'Receita (carregada)' : 'Receita travada'}
          value={formatCurrency(metrics.receita)} hint={parcial ? 'parcial — carregue mais' : null} />
        <StatCard icon={FaHourglassHalf} tone={metrics.media != null && metrics.media >= SLA_LIMITE_H ? 'warn' : 'default'}
          label="Espera média" value={metrics.media != null ? humanHours(metrics.media) : '—'} />
        <StatCard icon={FaClock} tone={metrics.maisAntigo != null && metrics.maisAntigo >= SLA_LIMITE_H ? 'warn' : 'default'}
          label="Mais antigo" value={metrics.maisAntigo != null ? humanHours(metrics.maisAntigo) : '—'} />
      </div>

      {/* ── Surpresa: próxima ação sugerida ── */}
      {priorityTicket && !query && (
        <button type="button" className="cc-nextbest" onClick={() => setExpandedId(priorityTicket.id)}>
          <span className="cc-nextbest__badge"><FaBolt size={12} /> Comece por aqui</span>
          <span className="cc-nextbest__txt">
            <strong>{priorityTicket.nome_empresa}</strong> espera há {humanHours(waitHoursOf(priorityTicket))} ·
            {' '}{formatCurrency(priorityTicket.valor_mensal)}/mês travados
          </span>
          <span className="cc-nextbest__go">Ver <FaChevronRight size={11} /></span>
        </button>
      )}

      {/* ── Abas ── */}
      <div className="cc-tabs">
        {GRUPOS.map((g) => {
          const Icon = g.icon;
          const ativa = tab === g.key;
          const n = counts[g.key] || 0;
          const badgePulse = g.key === 'pendentes' && pulse && !ativa;
          return (
            <button key={g.key} type="button" className={`cc-tab ${ativa ? 'is-active' : ''}`} onClick={() => setTab(g.key)}>
              <Icon size={12} /> {g.label}
              <span className={`cc-tab__badge ${badgePulse ? 'is-pulse' : ''}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* ── Busca + ordenação ── */}
      {!loading && items.length > 0 && (
        <div className="cc-toolbar">
          <div className="cc-search">
            <FaSearch size={12} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar empresa, responsável ou vendedor…  ( / )"
            />
            {query && <button type="button" className="cc-search__clear" onClick={() => setQuery('')}><FaTimesCircle size={12} /></button>}
          </div>
          <label className="cc-sortsel">
            <FaSort size={11} />
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
      )}

      {/* ── Lista ── */}
      {loading ? (
        <div className="cc-loading">
          <span className="cc-spin cc-spin--lg" /> Carregando Central de Conversões…
        </div>
      ) : visible.length === 0 ? (
        <div className="cc-empty">
          {query
            ? `Nenhum resultado para “${query}”.`
            : tab === 'pendentes'
              ? 'Nenhum ticket aguardando ação. Tudo em dia! 🎉'
              : tab === 'assinatura'
                ? 'Nenhuma conversão em processo de assinatura.'
                : 'Nenhuma conversão encerrada.'}
        </div>
      ) : (
        <div className="cc-list">
          {visible.map((c) => (
            <TicketCard
              key={c.id}
              conversao={c}
              onOpenBillingModal={handleOpenBillingModal}
              creating={creatingId === c.id}
              expanded={expandedId === c.id}
              onToggle={() => setExpandedId((prev) => (prev === c.id ? null : c.id))}
              onCopy={copyToClipboard}
              isPriority={c.id === priorityId}
              onReenviar={handleReenviar}
              reenviando={reenviandoId === c.id}
            />
          ))}

          {hasMore && !query && (
            <div className="cc-more">
              <button type="button" className="cc-btn cc-btn--ghost" disabled={loadingMore} onClick={() => fetchPage(tab, items.length, false)}>
                {loadingMore ? 'Carregando…' : `Carregar mais (${total - items.length} restante${total - items.length > 1 ? 's' : ''})`}
              </button>
            </div>
          )}
          {query && hasMore && (
            <p className="cc-hint-parcial">A busca cobre apenas os {items.length} tickets já carregados. Limpe a busca e carregue mais para ampliar.</p>
          )}
        </div>
      )}

      {/* ── Modal: início da cobrança (obrigatório antes de criar tenant) ── */}
      <BillingStartModal
        open={!!billingModalConversao}
        empresa={billingModalConversao?.nome_empresa || ''}
        submitting={!!billingModalConversao && creatingId === billingModalConversao.id}
        onConfirm={handleConfirmCreateTenant}
        onCancel={() => {
          if (creatingId) return; // não deixa fechar no meio da criação
          setBillingModalConversao(null);
        }}
      />
    </div>
  );
}